import React, { useState } from "react";
import { PremiumStepper } from "../components/brand/PremiumStepper";
import { PremiumRange } from "../components/brand/PremiumRange";
import { PremiumSegmented } from "../components/brand/PremiumSegmented";
import { PremiumSelect } from "../components/brand/PremiumSelect";
import { PremiumInput } from "../components/brand/PremiumInput";
import { PremiumTextarea } from "../components/brand/PremiumTextarea";

/**
 * ChainFlow OS · Brand Primitives — internal design surface.
 *
 * Living showroom for the foundational interaction primitives.
 * Not linked from anywhere in the marketing or operational chrome.
 * Use it to:
 *   1. Verify the locked v1 interaction grammar after any
 *      `useHoldRepeat` / `PremiumRange` change.
 *   2. Onboard new primitives — when a new brand-level interaction
 *      ships, mount one example here so contributors can feel the
 *      operational rhythm without spinning up a real consumer surface.
 *
 * Visited at: /dev/brand-primitives
 */
export default function BrandPrimitivesPage() {
  const [tx, setTx] = useState(40);
  const [fee, setFee] = useState(1500);
  const [conf, setConf] = useState(62);
  const [risk, setRisk] = useState(50);
  const [tolerance, setTolerance] = useState(15);
  const [interventionSensitivity, setInterventionSensitivity] = useState(70);
  const [exposureFloor, setExposureFloor] = useState(2500);

  // ── Segmented examples ──
  const [horizon, setHorizon] = useState("weekly");
  const [riskTier, setRiskTier] = useState("watching");
  const [mcMode, setMcMode] = useState("operational");
  const [stateFilter, setStateFilter] = useState("active");
  const [officeMode, setOfficeMode] = useState("month");

  // ── Select examples ──
  const [agentId, setAgentId] = useState(null);
  const [stage, setStage] = useState("offer_accepted");
  const [docCategory, setDocCategory] = useState(null);
  const [propertyType, setPropertyType] = useState("flat");
  const [solicitorId, setSolicitorId] = useState(null);

  // ── Input examples ──
  const [buyerName, setBuyerName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [fee2, setFee2] = useState("1500");
  const [completionDate, setCompletionDate] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [noteTitle, setNoteTitle] = useState("");
  const [askingPrice, setAskingPrice] = useState("");

  // ── Textarea examples ──
  const [opsNote, setOpsNote] = useState("");
  const [recoveryNote, setRecoveryNote] = useState(
    "Buyer's mortgage offer issued by NatWest on Tuesday. Solicitor confirmed enquiries closed Wednesday."
  );
  const [legalSummary, setLegalSummary] = useState("");

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 py-14 px-6">
      <div className="max-w-3xl mx-auto">
        <header className="mb-10">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.2em] text-violet-700 mb-2">
            Brand Interaction Primitives
          </p>
          <h1 className="font-display text-3xl font-bold tracking-[-0.012em] mb-2">
            ChainFlow OS · Operational Controls
          </h1>
          <p className="text-[13.5px] text-slate-600 leading-relaxed max-w-2xl">
            Every numeric and continuous-selection interaction on the
            platform is built on one of two primitives — both share an
            identical motion philosophy, reduced-motion behaviour, focus
            grammar and touch parity. This page is a living verification
            surface; never linked from chrome.
          </p>
        </header>

        <section className="rounded-2xl bg-white border border-slate-200 p-7 mb-7 shadow-sm">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1">
            PremiumStepper
          </p>
          <h2 className="font-display text-[18px] font-bold mb-1">
            Discrete numeric precision · press-and-hold acceleration
          </h2>
          <p className="text-[12.5px] text-slate-600 leading-relaxed mb-5">
            Tap = single step. Hold = settle at 380 ms then accelerate
            from 140 → 38 ms with the step multiplier ramping 1× → 6×.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-[10.5px] text-slate-500 mb-1.5">Monthly transactions</p>
              <PremiumStepper value={tx} onChange={setTx} lo={0} hi={500} step={5} testid="dev-tx" />
            </div>
            <div>
              <p className="text-[10.5px] text-slate-500 mb-1.5">Average fee per deal</p>
              <PremiumStepper value={fee} onChange={setFee} lo={200} hi={20000} step={100} prefix="£" testid="dev-fee" />
            </div>
          </div>
        </section>

        <section className="rounded-2xl bg-white border border-slate-200 p-7 shadow-sm">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1">
            PremiumRange
          </p>
          <h2 className="font-display text-[18px] font-bold mb-1">
            Continuous selection · click · drag · keyboard
          </h2>
          <p className="text-[12.5px] text-slate-600 leading-relaxed mb-6">
            Click on the track to snap. Drag the thumb for fine
            adjustment. Focused thumb accepts ← / → · ↑ / ↓ · PageUp /
            PageDown · Home / End. Every set is snapped to the step grid.
          </p>

          <div className="space-y-7">
            <PremiumRange
              label="Completion confidence cut-off"
              hint="Deals below this score escalate to the Director Centre intervention queue."
              value={conf}
              onChange={setConf}
              lo={0}
              hi={100}
              step={5}
              suffix="%"
              ticks={[20, 50, 80]}
              testid="dev-conf"
            />
            <PremiumRange
              label="Risk band threshold"
              value={risk}
              onChange={setRisk}
              lo={0}
              hi={100}
              step={1}
              suffix="%"
              testid="dev-risk"
            />
            <PremiumRange
              label="Forecasting tolerance"
              hint="Permitted variance before the weekly report flags drift."
              value={tolerance}
              onChange={setTolerance}
              lo={0}
              hi={30}
              step={1}
              suffix=" days"
              ticks={[7, 14, 21]}
              testid="dev-tol"
            />
            <PremiumRange
              label="Intervention sensitivity"
              hint="How aggressively the orchestration engine surfaces risks."
              value={interventionSensitivity}
              onChange={setInterventionSensitivity}
              lo={0}
              hi={100}
              step={5}
              suffix="%"
              testid="dev-sens"
            />
            <PremiumRange
              label="Revenue exposure floor"
              hint="Deals below this annual fee exposure are hidden from the revenue command centre."
              value={exposureFloor}
              onChange={setExposureFloor}
              lo={0}
              hi={25000}
              step={500}
              prefix="£"
              testid="dev-exp"
            />
            <PremiumRange
              label="Disabled example"
              value={50}
              onChange={() => {}}
              lo={0}
              hi={100}
              step={1}
              suffix="%"
              disabled
              testid="dev-disabled"
            />
          </div>
        </section>

        <section className="rounded-2xl bg-white border border-slate-200 p-7 mt-7 shadow-sm">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1">
            PremiumSegmented
          </p>
          <h2 className="font-display text-[18px] font-bold mb-1">
            Categorical selection · operational mode switching
          </h2>
          <p className="text-[12.5px] text-slate-600 leading-relaxed mb-6">
            Click or tap to select. Focused segment accepts ← / → · ↑ / ↓ ·
            Home / End · Space / Enter. The active pill slides behind the
            selected option in 280 ms; `prefers-reduced-motion` collapses
            the slide to an instant swap.
          </p>

          <div className="space-y-6">
            <div>
              <p className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-2">
                Timeframe switching
              </p>
              <PremiumSegmented
                value={horizon}
                onChange={setHorizon}
                ariaLabel="Time horizon"
                testid="dev-horizon"
                options={[
                  { value: "weekly",    label: "Weekly" },
                  { value: "monthly",   label: "Monthly" },
                  { value: "quarterly", label: "Quarterly" },
                ]}
              />
            </div>

            <div>
              <p className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-2">
                Risk severity tier
              </p>
              <PremiumSegmented
                value={riskTier}
                onChange={setRiskTier}
                ariaLabel="Risk severity tier"
                testid="dev-risk-tier"
                options={[
                  { value: "calm",      label: "Calm" },
                  { value: "watching",  label: "Watching" },
                  { value: "at-risk",   label: "At risk" },
                  { value: "slipping",  label: "Slipping" },
                ]}
              />
            </div>

            <div>
              <p className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-2">
                Mission Control mode
              </p>
              <PremiumSegmented
                value={mcMode}
                onChange={setMcMode}
                ariaLabel="Mission Control mode"
                testid="dev-mc-mode"
                size="lg"
                fullWidth
                options={[
                  { value: "operational", label: "Operational" },
                  { value: "forecast",    label: "Forecast" },
                  { value: "intervention", label: "Intervention" },
                  { value: "audit",       label: "Audit" },
                ]}
              />
            </div>

            <div>
              <p className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-2">
                Operational state filtering
              </p>
              <PremiumSegmented
                value={stateFilter}
                onChange={setStateFilter}
                ariaLabel="Operational state filter"
                testid="dev-state"
                options={[
                  { value: "all",        label: "All" },
                  { value: "active",     label: "Active" },
                  { value: "stalled",    label: "Stalled" },
                  { value: "exchanged",  label: "Exchanged", disabled: true },
                  { value: "completed",  label: "Completed" },
                ]}
              />
            </div>

            <div>
              <p className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-2">
                Office comparison
              </p>
              <PremiumSegmented
                value={officeMode}
                onChange={setOfficeMode}
                ariaLabel="Office comparison range"
                testid="dev-office"
                size="sm"
                options={[
                  { value: "week",    label: "7d" },
                  { value: "month",   label: "30d" },
                  { value: "quarter", label: "90d" },
                  { value: "year",    label: "12m" },
                ]}
              />
            </div>

            <div>
              <p className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-2">
                autoWidth (content-sized segments)
              </p>
              <PremiumSegmented
                value={riskTier}
                onChange={setRiskTier}
                ariaLabel="autoWidth example"
                testid="dev-auto"
                size="sm"
                autoWidth
                options={[
                  { value: "calm",     label: "Calm" },
                  { value: "watching", label: "Watching" },
                  { value: "at-risk",  label: "At risk" },
                  { value: "slipping", label: "Significantly slipping" },
                ]}
              />
            </div>

            <div>
              <p className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-2">
                Disabled example
              </p>
              <PremiumSegmented
                value="b"
                onChange={() => {}}
                disabled
                ariaLabel="Disabled example"
                testid="dev-seg-disabled"
                options={[
                  { value: "a", label: "Option A" },
                  { value: "b", label: "Option B" },
                  { value: "c", label: "Option C" },
                ]}
              />
            </div>
          </div>
        </section>

        <section className="rounded-2xl bg-white border border-slate-200 p-7 mt-7 shadow-sm">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1">
            PremiumSelect
          </p>
          <h2 className="font-display text-[18px] font-bold mb-1">
            High-cardinality selection · brand-level dropdown
          </h2>
          <p className="text-[12.5px] text-slate-600 leading-relaxed mb-6">
            Click trigger or press ↓ / Enter / Space to open. Focused
            listbox accepts ↓ / ↑ / Home / End · Enter / Space to select ·
            Esc / Tab / click-outside to close. Type-ahead jumps to the
            first matching label.
          </p>

          <div className="grid grid-cols-2 gap-5">
            <PremiumSelect
              label="Agent assignment"
              hint="Who will progress this transaction?"
              value={agentId}
              onChange={setAgentId}
              placeholder="Select an agent"
              searchable
              searchPlaceholder="Search agents…"
              clearable
              testid="dev-agent"
              options={[
                { value: "u1", label: "Aaliyah Wilson",  hint: "Manchester · Senior" },
                { value: "u2", label: "Ben Carter",      hint: "Manchester · Director" },
                { value: "u3", label: "Chloe Patel",     hint: "Leeds · Lead progressor" },
                { value: "u4", label: "Daniel Müller",   hint: "Manchester · Progressor" },
                { value: "u5", label: "Elena Rossi",     hint: "London · Senior" },
                { value: "u6", label: "Faisal Hussain",  hint: "Birmingham · Progressor" },
                { value: "u7", label: "Grace O'Connor",  hint: "Manchester · Lettings", disabled: true },
                { value: "u8", label: "Hiroshi Tanaka",  hint: "London · Director" },
                { value: "u9", label: "Isabella Costa",  hint: "Bristol · Senior" },
                { value: "u10", label: "Jasper Kowalski", hint: "Leeds · Progressor" },
                { value: "u11", label: "Kavi Sharma",    hint: "Manchester · Junior" },
                { value: "u12", label: "Lila Andersson", hint: "London · Lead progressor" },
              ]}
            />

            <PremiumSelect
              label="Deal stage"
              hint="Current operational position."
              value={stage}
              onChange={setStage}
              testid="dev-stage"
              options={[
                { value: "instruction",       label: "Instruction" },
                { value: "marketing",         label: "Marketing" },
                { value: "viewings",          label: "Viewings" },
                { value: "offers",            label: "Offers received" },
                { value: "offer_accepted",    label: "Offer accepted" },
                { value: "memo_issued",       label: "Memo of sale issued" },
                { value: "searches",          label: "Searches & enquiries" },
                { value: "mortgage_offer",    label: "Mortgage offer" },
                { value: "exchange_ready",    label: "Ready to exchange" },
                { value: "exchanged",         label: "Exchanged" },
                { value: "completion",        label: "Completion" },
                { value: "fall_through",      label: "Fallen through" },
              ]}
            />

            <PremiumSelect
              label="Property type"
              value={propertyType}
              onChange={setPropertyType}
              size="sm"
              testid="dev-prop-type"
              options={[
                { value: "flat",        label: "Apartment" },
                { value: "terraced",    label: "Terraced house" },
                { value: "semi",        label: "Semi-detached" },
                { value: "detached",    label: "Detached" },
                { value: "bungalow",    label: "Bungalow" },
                { value: "land",        label: "Land" },
                { value: "commercial",  label: "Commercial" },
              ]}
            />

            <PremiumSelect
              label="Solicitor / Conveyancer"
              hint="Searchable. Includes panel firms."
              value={solicitorId}
              onChange={setSolicitorId}
              placeholder="Select solicitor"
              searchable
              clearable
              testid="dev-solicitor"
              options={[
                { value: "s1",  label: "Thompson & Partners",   hint: "Manchester · Panel · Avg 32 days" },
                { value: "s2",  label: "Hawthorne Law",         hint: "London · Panel · Avg 28 days" },
                { value: "s3",  label: "Greenacre Legal",       hint: "Leeds · Avg 41 days" },
                { value: "s4",  label: "Marsh & Co Solicitors", hint: "Bristol · Avg 35 days" },
                { value: "s5",  label: "Pemberton Conveyancing", hint: "Manchester · Panel · Avg 26 days" },
                { value: "s6",  label: "Brookfield Law",        hint: "Birmingham · Avg 38 days" },
                { value: "s7",  label: "Hartwell Legal",        hint: "London · Panel · Avg 24 days" },
                { value: "s8",  label: "Ashcroft & Sons",       hint: "Manchester · Avg 44 days" },
                { value: "s9",  label: "Linden Conveyancing",   hint: "Leeds · Panel · Avg 27 days" },
                { value: "s10", label: "Northgate Solicitors",  hint: "Bristol · Avg 33 days" },
              ]}
            />

            <PremiumSelect
              label="Document category"
              hint="Required for upload routing."
              error={!docCategory ? "Required" : undefined}
              value={docCategory}
              onChange={setDocCategory}
              placeholder="Select category"
              testid="dev-doc-cat"
              options={[
                { value: "title_pack",     label: "Title pack" },
                { value: "memo",           label: "Memorandum of sale" },
                { value: "ews1",           label: "EWS1 / Cladding" },
                { value: "ta6",            label: "TA6 Property Information Form" },
                { value: "ta10",           label: "TA10 Fittings & Contents" },
                { value: "search_results", label: "Search results" },
                { value: "mortgage_offer", label: "Mortgage offer" },
              ]}
            />

            <PremiumSelect
              label="Disabled example"
              value="x"
              onChange={() => {}}
              disabled
              testid="dev-sel-disabled"
              options={[
                { value: "x", label: "Selected but disabled" },
                { value: "y", label: "Cannot reach" },
              ]}
            />
          </div>
        </section>

        <section className="rounded-2xl bg-white border border-slate-200 p-7 mt-7 shadow-sm">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1">
            PremiumInput
          </p>
          <h2 className="font-display text-[18px] font-bold mb-1">
            Free-form value entry · the form layer
          </h2>
          <p className="text-[12.5px] text-slate-600 leading-relaxed mb-6">
            Native <code className="font-mono text-[11.5px] text-slate-700">&lt;input&gt;</code>{" "}
            underneath for IME, autofill, and native mobile keyboards.
            Brand focus / hover / disabled / error grammar layered on
            top — shared with PremiumSelect so adjacent fields feel
            identical.
          </p>

          <div className="grid grid-cols-2 gap-5">
            <PremiumInput
              label="Buyer name"
              hint="As it appears on the offer letter."
              placeholder="e.g. Aaliyah Wilson"
              value={buyerName}
              onChange={setBuyerName}
              required
              clearable
              autoComplete="name"
              testid="dev-buyer-name"
            />

            <PremiumInput
              label="Email address"
              type="email"
              placeholder="you@example.com"
              hint="We'll use this for completion notifications."
              value={email}
              onChange={setEmail}
              error={email && !email.includes("@") ? "Enter a valid email address" : undefined}
              clearable
              autoComplete="email"
              testid="dev-email"
            />

            <PremiumInput
              label="Phone number"
              type="tel"
              placeholder="+44 7700 900000"
              value={phone}
              onChange={setPhone}
              autoComplete="tel"
              testid="dev-phone"
            />

            <PremiumInput
              label="Asking price"
              format="currency"
              placeholder="0"
              hint="Net of fees."
              value={askingPrice}
              onChange={setAskingPrice}
              testid="dev-asking"
            />

            <PremiumInput
              label="Average fee"
              format="currency"
              value={fee2}
              onChange={setFee2}
              size="sm"
              testid="dev-fee-input"
            />

            <PremiumInput
              label="Completion date"
              type="date"
              value={completionDate}
              onChange={setCompletionDate}
              testid="dev-completion-date"
            />

            <PremiumInput
              label="Search"
              format="search"
              placeholder="Search across deals, contacts, documents…"
              value={searchQuery}
              onChange={setSearchQuery}
              clearable
              testid="dev-search-input"
            />

            <PremiumInput
              label="Note title"
              placeholder="Loading…"
              value={noteTitle}
              onChange={setNoteTitle}
              loading
              testid="dev-note-loading"
            />

            <PremiumInput
              label="Readonly example"
              value="DEAL-2026-00471"
              onChange={() => {}}
              readOnly
              hint="System-generated identifier."
              testid="dev-readonly"
            />

            <PremiumInput
              label="Disabled example"
              value="Cannot edit while archived"
              onChange={() => {}}
              disabled
              testid="dev-inp-disabled"
            />
          </div>
        </section>

        <section className="rounded-2xl bg-white border border-slate-200 p-7 mt-7 shadow-sm">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1">
            PremiumTextarea
          </p>
          <h2 className="font-display text-[18px] font-bold mb-1">
            Multi-line value entry · operational notes
          </h2>
          <p className="text-[12.5px] text-slate-600 leading-relaxed mb-6">
            Native textarea underneath. Same focus ring, hover, error,
            disabled, and reduced-motion grammar as PremiumInput.
            Optional auto-resize, optional character count.
          </p>

          <div className="grid grid-cols-2 gap-5">
            <PremiumTextarea
              label="Operational note"
              hint="Visible to your team only."
              placeholder="What happened this week?"
              value={opsNote}
              onChange={setOpsNote}
              required
              autoResize
              minRows={2}
              maxRows={8}
              maxLength={500}
              showCount
              testid="dev-ops-note"
            />

            <PremiumTextarea
              label="Recovery narrative"
              hint="The story you'd tell on the Director call."
              value={recoveryNote}
              onChange={setRecoveryNote}
              autoResize
              testid="dev-recovery"
            />

            <PremiumTextarea
              label="Legal summary"
              error={legalSummary.length > 0 && legalSummary.length < 30 ? "Please provide at least 30 characters of context." : undefined}
              placeholder="Outline the legal status…"
              value={legalSummary}
              onChange={setLegalSummary}
              rows={3}
              maxLength={1000}
              showCount
              testid="dev-legal"
            />

            <PremiumTextarea
              label="Readonly example"
              value={"Solicitor handover sent 2026-02-11.\nAcknowledgement received 2026-02-12."}
              onChange={() => {}}
              readOnly
              hint="System-recorded handover log."
              testid="dev-ta-readonly"
            />

            <PremiumTextarea
              label="Loading example"
              value=""
              onChange={() => {}}
              loading
              hint="Generating AI draft…"
              testid="dev-ta-loading"
            />

            <PremiumTextarea
              label="Disabled example"
              value="Cannot edit while archived"
              onChange={() => {}}
              disabled
              testid="dev-ta-disabled"
            />
          </div>
        </section>
      </div>
    </div>
  );
}
